import { MainTitle, SpeakButton } from "components/atoms";
import { Bubbles } from "components/molecules";
import {
  BackButton,
  Card,
  CardTitle,
  Content,
  Page,
  TopBar,
  VisuallyHidden,
} from "components/templates";
import { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import styled from "styled-components";
import { useAppLanguage } from "context/AppLanguageContext";
import { extractCurrencyCode, useUsdConversionRate } from "hooks";
import SpanishData from "utils/SpanishData.json";
import EnglishData from "utils/EnglishData.json";
import countriesData from "utils/countriesData.json";
import {
  CountryField,
  groupCountryFields,
  isPairMap,
  PairMapValue as CountryPairMapValue,
  splitBlockMeta,
  splitPairsAndLists,
} from "utils/countryFields";
import {
  CountryClimate,
  describeClimate,
  isCountryClimate,
  scalePosition,
  SCALE_TICKS,
} from "utils/climate";
import {
  bestCountryMatch,
  parsedCountryName,
  resolveContinent,
} from "utils/functions";
import {
  countryHref,
  learnHref,
  readRouteParam,
  routeHref,
  topicHref,
} from "utils/learnPaths";
import { findRouteThread } from "utils/learnRoutes";
import { findLearnTopic } from "utils/learnTopics";
import {
  formatIndicatorNumber,
  indicatorContext,
  shortIndicatorValue,
} from "utils/worldIndicators";
import { AvailableLanguages } from "utils/types";

type CountryInfo = Record<string, unknown>;

const HeroCard = styled.section`
  display: flex;
  gap: 30px;
  align-items: center;
  padding: 28px 23px 28px 18px;
  border-radius: 20px;
  border: 1px solid rgba(245, 226, 200, 0.18);
  background: rgba(27, 38, 79, 0.22);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  flex-wrap: wrap;
`;

const Flag = styled.img`
  width: min(285px, 100%);
  height: auto;
  aspect-ratio: 285 / 160;
  flex: 0 1 auto;
  border-radius: 10px;
  border: 1px solid rgba(245, 226, 200, 0.28);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.35);
  background: rgba(0, 0, 0, 0.15);
`;

// The flag says which country this is if you already know the flag. The
// locator says where on earth it is, which is the part a geography app owes
// the reader. Drawn from the same paths the world map renders.
const Outline = styled.svg<{ $ready?: boolean }>`
  /* Square, because continents aren't: a portrait frame suits the Americas
     and wastes three quarters of itself on Asia. */
  width: 116px;
  height: 116px;
  flex: 0 0 auto;
  overflow: visible;
  /* Hidden for the one frame between "the whole world" and the measured
     crop, so the outline doesn't flash at world scale on every page load. */
  opacity: ${({ $ready }) => ($ready ? 1 : 0)};
  transition: opacity 160ms ease;

  & path {
    stroke: rgba(24, 40, 72, 0.55);
    stroke-width: 0.6;
    stroke-linejoin: round;
    vector-effect: non-scaling-stroke;
  }

  & .target {
    fill: rgba(245, 226, 200, 0.94);
  }

  /* In the locator the subject can be a speck — Malta is three units across
     in a frame two hundred wide. A non-scaling stroke drawn underneath the
     fill keeps it a visible dot at any zoom, which is what a locator has to
     guarantee. */
  & .highlight {
    stroke: rgba(232, 137, 74, 0.95);
    stroke-width: 3px;
    paint-order: stroke fill;
  }

  /* The rest of the continent, present but clearly not the subject. */
  & .peer {
    fill: rgba(245, 226, 200, 0.17);
    stroke: rgba(24, 40, 72, 0.35);
  }

  @media (max-width: 560px) {
    width: 88px;
    height: 88px;
  }
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  justify-content: space-around;

  /* Without a basis, this block's hypothetical size is its max-content width
     — and a pill listing Russia's fourteen neighbours is about 1000px on one
     line. The flex line then broke before the flag, dropping the title and
     pills underneath it with the whole right half of the card left empty.
     A 320px basis keeps the block beside the flag and lets the pills wrap
     inside it; it only moves below on a phone, where stacking is right. */
  flex: 1 1 320px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;

  /* MainTitle renders an h1; remove default margins for clean alignment. */
  & > h1 {
    margin: 0;
  }
`;

const PillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(173, 216, 230, 0.28);
  background: rgba(24, 40, 72, 0.42);
  color: rgba(245, 226, 200, 0.92);
  font-family: "Finger Paint", cursive;
  font-size: 0.9rem;
  letter-spacing: 0.2px;
  /* A pill is an inline-flex box, so its natural width is the whole list on
     one line; this lets it shrink and wrap instead of pushing the row wider
     than the card. */
  max-width: 100%;
`;

// Territories whose sovereignty is actively disputed get a visible pointer to
// the matching Learn article — the flag and quick facts above still reflect
// who currently administers the territory, this just surfaces the other
// side's claim instead of silently omitting it.
const SOVEREIGNTY_DISPUTE_LEARN_SLUG: Record<string, string> = {
  "Falkland Islands": "malvinas-falklands",
};

// The only way out of a country page was the back button, one press per step
// — and someone reading a route country by country had a lot of steps behind
// them. These are the same floating pills the home screen uses, so the
// shortcut looks like part of the app rather than a patch.
//
// Two, not three: the map is where browsing lives and Learn is where the
// reader came from. Autotest is a mode you choose deliberately, not
// something wanted mid-article, and a third pill would dilute the two that
// are actually useful here.
const Shortcuts = styled.nav`
  position: fixed;
  right: 18px;
  bottom: 14px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 560px) {
    right: 12px;
    left: 12px;
    bottom: 12px;
    justify-content: center;
  }
`;

const Shortcut = styled(Link)`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(245, 226, 200, 0.35);
  color: #f5e2c8;
  background: rgba(27, 38, 79, 0.62);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 10px 14px;
  border-radius: 999px;
  letter-spacing: 0.2px;
  font-family: "Finger Paint", cursive;
  font-size: 0.86rem;
  text-decoration: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition:
    transform 120ms ease,
    background 120ms ease,
    border-color 120ms ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(36, 60, 110, 0.78);
    border-color: rgba(245, 226, 200, 0.6);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

// --- the route you arrived on ---------------------------------------------
// A route is a story told across countries, and tapping a stop used to end
// it: the reader landed on a country page with nothing about coffee on it.
// This band keeps the thread — it says which story brought you here, where
// you are in it, and where it goes next, so the country data is read as part
// of the story rather than instead of it.
const Thread = styled.section`
  border-radius: 18px;
  border: 1px solid rgba(255, 209, 102, 0.34);
  background: linear-gradient(
    180deg,
    rgba(255, 209, 102, 0.12),
    rgba(27, 38, 79, 0.28)
  );
  padding: 14px 16px 15px;
`;

const ThreadTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 10px;
`;

const ThreadEyebrow = styled.span`
  font-family: "Finger Paint", cursive;
  font-size: 0.72rem;
  letter-spacing: 0.7px;
  color: rgba(245, 226, 200, 0.6);
`;

const ThreadRoute = styled(Link)`
  font-family: "Gaegu", cursive;
  font-size: 1.2rem;
  color: #f5e2c8;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: rgba(255, 209, 102, 0.5);

  &:hover {
    text-decoration-color: rgba(255, 209, 102, 0.95);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 3px;
    border-radius: 6px;
  }
`;

const ThreadPosition = styled.span`
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  color: rgba(245, 226, 200, 0.62);
`;

const ThreadTitle = styled.p`
  margin: 9px 0 0;
  font-family: "Finger Paint", cursive;
  font-size: 1rem;
  line-height: 1.35;
  color: rgba(245, 226, 200, 0.95);
`;

const ThreadBody = styled.p`
  margin: 7px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 1.2rem;
  line-height: 1.3;
  color: rgba(245, 226, 200, 0.9);
  max-width: 78ch;
`;

const ThreadNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 13px;
`;

const ThreadStep = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 13px 7px;
  border-radius: 999px;
  border: 1px solid rgba(255, 209, 102, 0.42);
  background: rgba(27, 38, 79, 0.42);
  color: #f5e2c8;
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  text-decoration: none;
  transition:
    background 120ms ease,
    transform 120ms ease;

  &:hover {
    background: rgba(255, 209, 102, 0.2);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const DisputeBanner = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid rgba(255, 209, 102, 0.4);
  background: rgba(255, 209, 102, 0.1);
  color: rgba(245, 226, 200, 0.95);
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  line-height: 1.3;
  text-decoration: none;
  transition:
    transform 120ms ease,
    background 120ms ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 209, 102, 0.18);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

// The page used to cap itself at the shared 1120px shell, which left ~800px
// of empty gradient on a desktop screen while the data was squeezed into one
// narrow scrolling column. Country pages are data-dense, so they get a wider
// shell than the reading-oriented routes (Learn, Autotest).
const WideContent = styled(Content)`
  max-width: 1480px;
  /* Room for the floating shortcuts, so the last card isn't sitting under
     them once the page is scrolled to the bottom. */
  padding-bottom: 92px;

  @media (max-width: 560px) {
    padding-bottom: 104px;
  }
`;

// One rhythm for the whole page. Cards used to carry their own margins
// (18px here, 16px there, 0 on the full-width ones), which glued the
// indicator strip to the quick facts and the two heritage cards to each
// other and to the divisions block. A flex column with a single gap makes
// every seam identical and leaves nothing to forget on a new card.
const BLOCK_GAP = 18;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${BLOCK_GAP}px;
  margin-top: ${BLOCK_GAP}px;
`;

// auto-fit rather than a fixed 2-up: the eight quick facts spread into four
// columns on a desktop screen and collapse to one on a phone, with no
// breakpoint bookkeeping.
const FactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 10px;
  /* Tiles stretch to their row (the grid default): every box in a row ends
     on the same line and every seam is exactly the gap. What used to make
     that unbearable — Spain's 390-character language note leaving 310px of
     empty box in the tiles beside it — is handled upstream instead: a value
     that long takes a row to itself, so the tiles that do share a row differ
     by a line or two at most.
     Dense flow backfills the hole a full-row tile would otherwise leave
     behind it when it doesn't fit in the current row. */
  grid-auto-flow: dense;
`;

// Multi-column rather than a grid: these cards have wildly different heights
// (Geography carries six fields, Government two), and a grid row stretches to
// its tallest member, which left exactly the kind of dead space this redesign
// is meant to remove. Multi-column packs them and balances column heights.
//
// 500px caps the layout at two columns (three would need 1536px, past the
// 1480px shell). Narrow columns were the root of the untidiness: a 390px card
// can only stack its fields one under the other, so Geography towered over
// Government beside it. At ~730px each field row fits two or three fields and
// the cards come out short and close in height.
const SectionsMasonry = styled.div`
  columns: 500px;
  /* A multi-column flow has no row gap, so the cards carry their own vertical
     spacing (see SectionCard). The container's height stops at the lowest
     card's border — a margin at the end of a column box is truncated — so
     nothing needs cancelling here, and the stack's gap sets the seam after
     the masonry like every other seam on the page. */
  column-gap: ${BLOCK_GAP}px;
`;

const SectionCard = styled(Card)`
  /* Without this a card can be split across two columns mid-content. */
  break-inside: avoid;
  margin-bottom: ${BLOCK_GAP}px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  & > h2 {
    margin: 0;
  }
`;

// The divisions line ("23 provinces and one Autonomous City") used to be a
// field box of its own, stacked above the capitals: a full-width card holding
// one short sentence. It reads as what it is — a subtitle for the grid below.
const SectionSubtitle = styled.p`
  margin: -4px 0 12px;
  font-family: "Gaegu", cursive;
  font-size: 1.25rem;
  line-height: 1.3;
  color: rgba(245, 226, 200, 0.85);
`;

const SectionEmoji = styled.span`
  font-size: 1.5rem;
  line-height: 1;
`;

const Fact = styled.div<{ $wide?: boolean }>`
  /* A column so the comparison line can be pushed to the bottom edge with
     margin-top: auto, instead of floating wherever the text above ends. */
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid rgba(173, 216, 230, 0.22);
  background: rgba(24, 40, 72, 0.28);
  padding: 10px 12px;

  /* A handful of facts are a paragraph rather than a value (Spain lists six
     co-official languages). Those take the whole row: at two columns out of
     four they were still four times taller than the tiles beside them, and
     since a row is as tall as its tallest member, one paragraph was setting
     the height of everything next to it. */
  @media (min-width: 720px) {
    ${({ $wide }) => $wide && "grid-column: 1 / -1;"}
  }
`;

// Where the chosen comparison country is remembered.
const COMPARE_KEY = "atlaxis.compareCountry";

// Past this a quick fact is prose, not a value. Half the values in the
// dataset are under 16 characters and 95% are under 80, so this pulls out
// the genuine paragraphs and leaves the rest sharing rows.
const WIDE_FACT_CHARS = 90;

const FactLabel = styled.p`
  margin: 0;
  font-family: "Finger Paint", cursive;
  font-size: 0.95rem;
  color: rgba(245, 226, 200, 0.8);
  letter-spacing: 0.2px;
`;

const FactValue = styled.p`
  margin: 6px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 1.25rem;
  color: rgba(245, 226, 200, 0.95);
  line-height: 1.2;
`;

// --- a figure against the rest of the world -------------------------------
// The bar is a percentile, not the raw value: GDP per capita runs from $700
// to $140,000, so a linear scale would pin four fifths of the world against
// the left edge. A percentile spreads countries evenly and puts the median
// at the midpoint of every bar, which is what makes two indicators
// comparable at a glance.
const MedianTrack = styled.div`
  position: relative;
  height: 7px;
  margin-top: 8px;
  border-radius: 999px;
  background: rgba(16, 28, 56, 0.6);
  border: 1px solid rgba(173, 216, 230, 0.2);
  overflow: hidden;
`;

const MedianFill = styled.div<{ $above: boolean }>`
  position: absolute;
  inset-block: 0;
  left: 0;
  border-radius: 999px;
  background: ${({ $above }) =>
    $above ? "rgba(232, 137, 74, 0.9)" : "rgba(111, 168, 220, 0.9)"};
`;

const MedianMark = styled.span`
  position: absolute;
  inset-block: -2px;
  left: 50%;
  width: 2px;
  background: rgba(245, 226, 200, 0.85);
`;

// The world median tells you whether a figure is high. Another country tells
// you what it means: nobody learns geography thinking "Argentina versus the
// median", they think "Argentina versus Chile".
const CompareMark = styled.span`
  position: absolute;
  inset-block: -3px;
  width: 2px;
  background: rgba(199, 146, 234, 0.95);
  box-shadow: 0 0 0 1px rgba(16, 28, 56, 0.6);
`;

const CompareRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px;
`;

const CompareLabel = styled.label`
  font-family: "Finger Paint", cursive;
  font-size: 0.78rem;
  letter-spacing: 0.4px;
  color: rgba(245, 226, 200, 0.6);
`;

// The native arrow sits flush against the border and no amount of
// padding-right moves it, so the control draws its own and reserves the room
// for it.
const CHEVRON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23f5e2c8' stroke-opacity='0.65' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E";

const CompareSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  color: #f5e2c8;
  background-color: rgba(24, 40, 72, 0.6);
  background-image: url("${CHEVRON}");
  background-repeat: no-repeat;
  background-position: right 12px center;
  border: 1px solid rgba(173, 216, 230, 0.3);
  border-radius: 999px;
  padding: 3px 32px 5px 13px;
  max-width: 100%;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }

  /* The open list is drawn by the operating system, so for years the only
     thing a page could change was an option's colour, and macOS ignored even
     that. The base-select appearance hands the picker over to CSS. Chrome
     only for now; everywhere else this block is skipped and the control
     above still looks right. */
  @supports (appearance: base-select) {
    appearance: base-select;
    background-image: none;
    padding-right: 13px;

    &::picker-icon {
      color: rgba(245, 226, 200, 0.62);
      transition: rotate 160ms ease;
    }

    &:open::picker-icon {
      rotate: 180deg;
    }

    &::picker(select) {
      appearance: base-select;
      margin-top: 6px;
      padding: 6px;
      max-height: 320px;
      border: 1px solid rgba(173, 216, 230, 0.28);
      border-radius: 16px;
      background: rgba(20, 32, 62, 0.97);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
      backdrop-filter: blur(10px);
    }

    & option {
      font-family: "Gaegu", cursive;
      font-size: 1.05rem;
      color: rgba(245, 226, 200, 0.88);
      padding: 4px 10px;
      border-radius: 10px;
      transition: background 100ms ease;
    }

    & option:hover,
    & option:focus {
      background: rgba(38, 62, 112, 0.9);
      color: #f5e2c8;
    }

    & option:checked {
      color: #f5e2c8;
    }
  }

  /* Everywhere else: the one property the platform picker honours. */
  & option {
    background: #1b264f;
    color: #f5e2c8;
  }
`;

// A line of its own, pinned to the bottom of the tile. Sharing the line with
// the world median let it sit beside or below depending on how long the
// median text happened to be, so the same comparison landed in a different
// place on every tile of the same row.
const CompareNote = styled.p`
  margin: auto 0 0;
  padding-top: 5px;
  font-family: "Gaegu", cursive;
  font-size: 0.95rem;
  line-height: 1.2;
  color: rgba(199, 146, 234, 0.92);
`;

const MedianNote = styled.p`
  margin: 4px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 0.95rem;
  line-height: 1.2;
  color: rgba(245, 226, 200, 0.58);
`;

// The three indicators where the country sits furthest from the middle.
// Picked by distance from the median, not by any judgement about which
// direction is good: the reader draws that conclusion.
const Unusual = styled.div`
  display: grid;
  gap: 6px;
  margin-bottom: 14px;
`;

const UnusualLine = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 9px;
  margin: 0;
  border-left: 2px solid rgba(232, 137, 74, 0.75);
  padding-left: 11px;
  font-family: "Gaegu", cursive;
  font-size: 1.1rem;
  line-height: 1.25;
  color: rgba(245, 226, 200, 0.72);

  & b {
    font-weight: 400;
    color: rgba(245, 226, 200, 0.98);
    font-variant-numeric: tabular-nums;
  }
`;

// Separates the two halves of the merged card: reference facts above,
// measured figures below. A rule rather than a second card, so the page
// keeps one block where it used to have two.
const GroupLabel = styled.p`
  margin: 18px 0 12px;
  padding-top: 14px;
  border-top: 1px solid rgba(245, 226, 200, 0.14);
  font-family: "Finger Paint", cursive;
  font-size: 0.78rem;
  letter-spacing: 0.4px;
  color: rgba(245, 226, 200, 0.6);
`;

const UnusualTitle = styled.p`
  margin: 0 0 2px;
  font-family: "Finger Paint", cursive;
  font-size: 0.8rem;
  letter-spacing: 0.3px;
  color: rgba(245, 226, 200, 0.6);
`;

const ConverterRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const ConverterInput = styled.input`
  width: 72px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid rgba(173, 216, 230, 0.35);
  background: rgba(24, 40, 72, 0.35);
  color: #f5e2c8;
  font-family: "Gaegu", cursive;
  font-size: 1rem;

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const ConverterEquals = styled.span`
  font-family: "Gaegu", cursive;
  color: rgba(245, 226, 200, 0.65);
  font-size: 1rem;
`;

const ConverterResult = styled.span`
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  color: rgba(245, 226, 200, 0.95);
`;

// Fields flow across the card instead of stacking. This is what shortens the
// cards enough for two columns to end near the same height: Malta's Geography
// went from five stacked boxes to three rows of two.
const FieldList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 10px;
`;

const FieldBlock = styled.article<{ $wide?: boolean; $bare?: boolean }>`
  min-width: 0;

  /* A field that is alone under its own heading doesn't need a box inside a
     box: the divisions grid was a bordered panel inside a bordered card. */
  ${({ $bare }) =>
    !$bare &&
    `
    border-radius: 14px;
    border: 1px solid rgba(245, 226, 200, 0.14);
    background: rgba(24, 40, 72, 0.18);
    padding: 12px 12px 10px;
  `}

  /* Prose and structured blocks (Music's genres + instruments) take the whole
     row: squeezed into one 250px track they tower over the value beside them,
     and the row stretches to match. */
  ${({ $wide }) => $wide && "grid-column: 1 / -1;"}
`;

// Past this many characters a field is prose, not a value.
const WIDE_FIELD_CHARS = 120;

// How much room a field's value will ask for, in characters. Objects are
// always laid out as their own sub-grid, so they get the full row regardless.
const fieldWantsFullRow = (value: unknown): boolean => {
  if (typeof value === "string") return value.length > WIDE_FIELD_CHARS;
  if (Array.isArray(value))
    return value.join(", ").length > WIDE_FIELD_CHARS || value.length > 3;
  return typeof value === "object" && value !== null;
};

const FieldName = styled.h3`
  margin: 0 0 6px;
  font-family: "Finger Paint", cursive;
  font-size: 1rem;
  letter-spacing: 0.2px;
  color: rgba(245, 226, 200, 0.9);
`;

// Province/state → capital pairs. A full-width auto-fill grid turns the
// worst offender (Russia, 86 pairs) from an 280px-tall inner scrollbox into
// a readable six-column block.
const PairGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
  margin-top: 4px;
`;

const PairTile = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(173, 216, 230, 0.18);
  background: rgba(24, 40, 72, 0.3);
  padding: 8px 10px;
  min-width: 0;
`;

const PairName = styled.p`
  margin: 0;
  font-family: "Finger Paint", cursive;
  font-size: 0.78rem;
  letter-spacing: 0.2px;
  color: rgba(245, 226, 200, 0.7);
  overflow-wrap: anywhere;
`;

const PairValue = styled.p`
  margin: 3px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  line-height: 1.15;
  color: rgba(245, 226, 200, 0.98);
  overflow-wrap: anywhere;
`;

// Prose describing a whole dict-valued block rather than one of its entries.
// Full width, no tile chrome: as a PairTile this dropped a paragraph into a
// 200px grid cell and stretched the whole row.
const BlockCaption = styled.p`
  margin: 0 0 10px;
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  line-height: 1.35;
  color: rgba(245, 226, 200, 0.85);
`;

const BlockNote = styled.p`
  margin: 10px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 1.02rem;
  line-height: 1.3;
  color: rgba(245, 226, 200, 0.68);
`;

// A sub-grouping inside a divisions block ("Local councils (68)"). Its own
// heading plus a dense full-width grid, so 68 short names read across the
// page instead of down two narrow columns.
const ListBlock = styled.div`
  margin-top: 14px;
`;

// const ListBlockLabel = styled.p`
//   margin: 0 0 6px;
//   font-family: "Finger Paint", cursive;
//   font-size: 0.82rem;
//   letter-spacing: 0.2px;
//   color: rgba(245, 226, 200, 0.75);
// `;

// `$min` because the two callers hold very different text: 68 council names
// of one or two words, and UNESCO titles that run to a full line.
const ItemGrid = styled.div<{ $min?: number }>`
  display: grid;
  grid-template-columns: ${({ $min }) =>
    `repeat(auto-fill, minmax(${$min ?? 150}px, 1fr))`};
  gap: 6px;
`;

const ItemTile = styled.span`
  border-radius: 10px;
  border: 1px solid rgba(173, 216, 230, 0.16);
  background: rgba(24, 40, 72, 0.26);
  padding: 6px 9px;
  min-width: 0;
  font-family: "Gaegu", cursive;
  font-size: 1.08rem;
  line-height: 1.2;
  color: rgba(245, 226, 200, 0.95);
  overflow-wrap: anywhere;
`;

const ExpandButton = styled.button`
  cursor: pointer;
  margin-top: 10px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid rgba(245, 226, 200, 0.3);
  background: rgba(27, 38, 79, 0.35);
  color: #f5e2c8;
  font-family: "Finger Paint", cursive;
  font-size: 0.82rem;
  transition:
    transform 120ms ease,
    background 120ms ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(27, 38, 79, 0.55);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

// --- climate -------------------------------------------------------------
// The two temperatures are the point of the block, and a number alone doesn't
// say whether -5 °C is a normal winter or a remarkable one. The band puts
// every country on one scale, so Oslo and Cairo can be compared by eye before
// either figure is read.
const Band = styled.div`
  position: relative;
  height: 13px;
  margin-top: 12px;
  border-radius: 999px;
  border: 1px solid rgba(173, 216, 230, 0.24);
  background: rgba(16, 28, 56, 0.55);
  overflow: hidden;
`;

const BandFill = styled.div`
  position: absolute;
  inset-block: 0;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(111, 168, 220, 0.95),
    rgba(203, 178, 122, 0.95) 55%,
    rgba(232, 137, 74, 0.95)
  );
`;

const BandScale = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 3px;
  font-family: "Gaegu", cursive;
  font-size: 0.9rem;
  color: rgba(245, 226, 200, 0.45);
  font-variant-numeric: tabular-nums;
`;

const BandEnds = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
`;

const BandEnd = styled.div<{ $align: "start" | "end" }>`
  text-align: ${({ $align }) => ($align === "end" ? "right" : "left")};
  min-width: 0;
`;

const BandTemp = styled.p<{ $tone: "cold" | "warm" }>`
  margin: 0;
  font-family: "Gaegu", cursive;
  font-size: 1.45rem;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => ($tone === "cold" ? "#8fc2e8" : "#f0a06a")};
`;

const BandCaption = styled.p`
  margin: 1px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 1rem;
  line-height: 1.2;
  color: rgba(245, 226, 200, 0.72);
`;

const InlineFacts = styled.p`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin: 11px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 1.1rem;
  line-height: 1.25;
  color: rgba(245, 226, 200, 0.78);

  & b {
    font-weight: 400;
    color: rgba(245, 226, 200, 0.98);
    font-variant-numeric: tabular-nums;
  }
`;

const ValueText = styled.p`
  margin: 0;
  font-family: "Gaegu", cursive;
  font-size: 1.25rem;
  color: rgba(245, 226, 200, 0.95);
  line-height: 1.25;
  word-break: break-word;
`;

const BulletList = styled.ul`
  margin: 6px 0 0 18px;
  padding: 0;
  display: grid;
  gap: 6px;
`;

const BulletItem = styled.li`
  margin: 0;
  padding: 0;
`;

const BulletText = styled.span`
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  color: rgba(245, 226, 200, 0.98);
  line-height: 1.25;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(173, 216, 230, 0.22);
  background: rgba(24, 40, 72, 0.32);
  color: rgba(245, 226, 200, 0.95);
  font-family: "Gaegu", cursive;
  font-size: 1.1rem;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const Details = styled.details`
  border-radius: 14px;
  border: 1px solid rgba(173, 216, 230, 0.18);
  background: rgba(24, 40, 72, 0.22);
  padding: 10px 12px;
`;

const Summary = styled.summary`
  cursor: pointer;
  user-select: none;
  font-family: "Finger Paint", cursive;
  color: rgba(245, 226, 200, 0.92);
  letter-spacing: 0.2px;
  font-size: 0.95rem;

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 3px;
    border-radius: 12px;
  }
`;

// auto-fit, not auto-fill: Music has exactly two rows (genres, instruments),
// and auto-fill left a third empty track hanging off the right.
const DefinitionList = styled.dl`
  margin: 10px 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 8px;
`;

const DT = styled.dt`
  font-family: "Finger Paint", cursive;
  color: rgba(245, 226, 200, 0.85);
  font-size: 0.95rem;
  margin: 0;
`;

const DD = styled.dd`
  margin: 2px 0 0;
  font-family: "Gaegu", cursive;
  color: rgba(245, 226, 200, 0.98);
  font-size: 1.15rem;
  line-height: 1.15;
`;

function findCountryKey(countryName: string):
  | {
      spanishName: string;
      englishName: string;
      initials?: string;
      source: "spanish" | "english";
    }
  | undefined {
  const decodedName = (() => {
    try {
      return decodeURIComponent(countryName);
    } catch {
      return countryName;
    }
  })();

  const normalizedInput = parsedCountryName(decodedName);

  const match = countriesData.find(
    (c: any) =>
      parsedCountryName(c["spanish-name"] ?? "") === normalizedInput ||
      parsedCountryName(c["english-name"] ?? "") === normalizedInput
  );

  const resolvedMatch =
    match ??
    bestCountryMatch(countriesData as any, decodedName)?.country ??
    undefined;

  if (!resolvedMatch) return undefined;

  const spanishName = resolvedMatch["spanish-name"];
  const englishName = resolvedMatch["english-name"];
  const initials =
    typeof resolvedMatch["initials"] === "string"
      ? resolvedMatch["initials"]
      : undefined;

  const source =
    parsedCountryName(resolvedMatch["english-name"] ?? "") === normalizedInput
      ? "english"
      : parsedCountryName(resolvedMatch["spanish-name"] ?? "") ===
          normalizedInput
        ? "spanish"
        : bestCountryMatch(countriesData as any, decodedName)?.matchedField ===
            "spanish"
          ? "spanish"
          : "english";

  return { spanishName, englishName, initials, source };
}

const currencyConverterCopy = {
  en: {
    amountLabel: (code: string) => `Amount to convert, in ${code}`,
    loading: "Loading rate…",
    error: "Rate unavailable",
  },
  es: {
    amountLabel: (code: string) => `Monto a convertir, en ${code}`,
    loading: "Cargando cotización…",
    error: "Cotización no disponible",
  },
} as const;

const formatUsd = (value: number, locale: string) => {
  // Weak currencies convert tiny amounts to fractions of a cent; rounding
  // those to 2 decimals always displays as "0", so scale precision to the
  // magnitude of the result instead of a fixed number of decimals.
  const decimals = value === 0 ? 2 : value >= 1 ? 2 : value >= 0.01 ? 4 : 6;
  return value.toLocaleString(locale, { maximumFractionDigits: decimals });
};

const CurrencyToUsd = ({
  code,
  isSpanish,
}: {
  code: string;
  isSpanish: boolean;
}) => {
  const [amount, setAmount] = useState("1");
  const state = useUsdConversionRate(code);
  const c = isSpanish ? currencyConverterCopy.es : currencyConverterCopy.en;

  if (code === "USD") return null;

  const numericAmount = Number(amount);
  const hasValidAmount =
    amount.trim() !== "" &&
    Number.isFinite(numericAmount) &&
    numericAmount >= 0;
  const inputId = `usd-conversion-${code}`;

  return (
    <ConverterRow>
      <label htmlFor={inputId}>
        <VisuallyHidden>{c.amountLabel(code)}</VisuallyHidden>
      </label>
      <ConverterInput
        id={inputId}
        type="number"
        min={0}
        inputMode="decimal"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        aria-label={c.amountLabel(code)}
      />
      <ConverterEquals aria-hidden="true">{code} ≈</ConverterEquals>
      <ConverterResult role="status" aria-live="polite">
        {state.status === "loading" && c.loading}
        {state.status === "error" && c.error}
        {state.status === "success" &&
          hasValidAmount &&
          `${formatUsd(
            numericAmount * state.rate,
            isSpanish ? "es-AR" : "en-US"
          )} USD`}
      </ConverterResult>
    </ConverterRow>
  );
};

// Past this many pairs the block is folded down to a preview with a toggle —
// long enough that most countries show in full, short enough that Russia's 86
// regions don't push everything else off the screen.
const PAIR_PREVIEW_COUNT = 24;

// Below this many entries a block is laid out inside the masonry instead of
// as a full-width strip: at 1400px a strip holds six or seven tiles per row,
// so anything shorter than two rows is mostly empty card.
const WIDE_LIST_MIN_ITEMS = 12;

// UNESCO titles run to a full line ("Il-Ftira, culinary art and culture of
// flattened sourdough bread in Malta"); council names are one or two words.
const HERITAGE_TILE_MIN = 230;

// One sub-grouping's members, collapsed past the preview count. Each list
// keeps its own expanded state, so opening the 68 local councils doesn't also
// unfold the 14 Gozo ones.
const CollapsibleItemGrid = ({
  label,
  items,
  showAllLabel,
  showLessLabel,
  minTile,
}: {
  label?: string;
  items: string[];
  showAllLabel: (n: number) => string;
  showLessLabel: string;
  minTile?: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isTruncated = items.length > PAIR_PREVIEW_COUNT;
  const visible =
    isTruncated && !expanded ? items.slice(0, PAIR_PREVIEW_COUNT) : items;

  return (
    <ListBlock>
      {/* <ListBlockLabel>{label}</ListBlockLabel> */}
      <ItemGrid $min={minTile}>
        {visible.map((item) => (
          <ItemTile key={item}>{item}</ItemTile>
        ))}
      </ItemGrid>
      {isTruncated && (
        <ExpandButton
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
        >
          {expanded ? showLessLabel : showAllLabel(items.length)}
        </ExpandButton>
      )}
    </ListBlock>
  );
};

// A shape cropped to whatever it contains. The extent is only knowable once
// the browser has laid the paths out — the same getBBox the world map uses
// for its continent crops — so it renders once at world scale, invisible,
// and settles on the measured box a frame later.
const CroppedOutline = ({
  label,
  signature,
  children,
}: {
  label: string;
  /** Changes when the contents change, so the crop is re-measured. */
  signature: string;
  children: React.ReactNode;
}) => {
  const groupRef = useRef<SVGGElement | null>(null);
  const [viewBox, setViewBox] = useState<string>();

  useEffect(() => {
    let frame = 0;
    let attempts = 0;

    const measure = () => {
      const node = groupRef.current;
      if (!node) return;
      let box: DOMRect;
      try {
        box = node.getBBox();
      } catch {
        // getBBox throws on a node that isn't rendered yet.
        box = new DOMRect();
      }
      // An empty box means the browser hasn't laid the paths out yet, which
      // happens often enough on a cold load to matter: without the retry the
      // outline simply never appears.
      if (!box.width || !box.height) {
        if (attempts++ < 10) frame = requestAnimationFrame(measure);
        return;
      }
      const pad = Math.max(box.width, box.height) * 0.08;
      const x = box.x - pad;
      const y = box.y - pad;
      const width = box.width + pad * 2;
      const height = box.height + pad * 2;
      // Square frame: fit the longer side and centre the shorter one.
      const side = Math.max(width, height);
      setViewBox(
        `${x - (side - width) / 2} ${y - (side - height) / 2} ${side} ${side}`
      );
    };

    setViewBox(undefined);
    measure();
    return () => cancelAnimationFrame(frame);
  }, [signature]);

  return (
    <Outline
      viewBox={viewBox ?? "0 0 1009 652"}
      role="img"
      aria-label={label}
      $ready={!!viewBox}
    >
      <g ref={groupRef}>{children}</g>
    </Outline>
  );
};

// Cropped to itself, a country's own outline teaches shape and nothing else,
// and for the small ones it doesn't even manage that: at the map's level of
// detail Cabo Verde comes out a circle and Malta a blob. The locator shows it
// inside its continent instead, which answers the questions the shape can't —
// where it is, and how big it is next to its neighbours.
type ContinentMember = { englishName: string; d: string };

const continentGroups = new Map<string, ContinentMember[]>();

const continentGroupFor = (englishName: string): ContinentMember[] => {
  if (continentGroups.size === 0) {
    (countriesData as any[]).forEach((country) => {
      if (country["not-a-country"] || typeof country.d !== "string") return;
      const info = (EnglishData as Record<string, Record<string, unknown>>)[
        country["english-name"]
      ];
      const continent = resolveContinent(
        country["english-name"],
        typeof info?.["Continent"] === "string"
          ? (info["Continent"] as string)
          : undefined
      );
      if (!continent) return;
      const bucket = continentGroups.get(continent);
      const member = { englishName: country["english-name"], d: country.d };
      if (bucket) bucket.push(member);
      else continentGroups.set(continent, [member]);
    });
  }

  for (const members of Array.from(continentGroups.values())) {
    if (members.some((member) => member.englishName === englishName)) {
      return members;
    }
  }
  return [];
};

const ContinentLocator = ({
  englishName,
  label,
}: {
  englishName: string;
  label: string;
}) => {
  const members = continentGroupFor(englishName);
  // One country alone is not a locator, it's the silhouette again.
  if (members.length < 2) return null;

  return (
    <CroppedOutline label={label} signature={`locator:${englishName}`}>
      {members.map((member) => (
        <path
          key={member.englishName}
          d={member.d}
          className={
            member.englishName === englishName ? "target highlight" : "peer"
          }
        />
      ))}
    </CroppedOutline>
  );
};

const ClimateValue = ({
  climate,
  isSpanish,
}: {
  climate: CountryClimate;
  isSpanish: boolean;
}) => {
  const copy = describeClimate(climate, isSpanish);
  const left = scalePosition(climate.low);
  const right = scalePosition(climate.high);

  return (
    <>
      {climate.detail && <ValueText>{climate.detail}</ValueText>}

      <Band
        role="img"
        aria-label={`${copy.low} ${copy.lowMonth}, ${copy.high} ${copy.highMonth}`}
      >
        <BandFill style={{ left: `${left}%`, right: `${100 - right}%` }} />
      </Band>
      <BandScale aria-hidden="true">
        {SCALE_TICKS.map((tick) => (
          <span key={tick}>{tick}°</span>
        ))}
      </BandScale>

      <BandEnds>
        <BandEnd $align="start">
          <BandTemp $tone="cold">{copy.low}</BandTemp>
          <BandCaption>{copy.lowMonth}</BandCaption>
        </BandEnd>
        <BandEnd $align="end">
          <BandTemp $tone="warm">{copy.high}</BandTemp>
          <BandCaption>{copy.highMonth}</BandCaption>
        </BandEnd>
      </BandEnds>

      <InlineFacts>
        <span>{copy.rain}</span>
        <span>
          {isSpanish ? "Clima " : "Climate is "}
          <b>{copy.humidity}</b>
        </span>
        <span>
          {copy.sunsetPrefix}
          <b>{copy.sunsetValue}</b>
          {copy.sunsetSuffix}
        </span>
      </InlineFacts>

      <BlockNote>{copy.note}</BlockNote>
    </>
  );
};

const PairMapValue = ({
  pairs,
  showAllLabel,
  showLessLabel,
}: {
  pairs: Record<string, CountryPairMapValue>;
  showAllLabel: (n: number) => string;
  showLessLabel: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const { captions, notes, entries } = splitBlockMeta(pairs);
  // A few countries mix seats with sub-lists of smaller divisions; each half
  // gets its own layout instead of dragging the block into a generic list.
  const { pairs: pairEntries, lists } = splitPairsAndLists(entries);
  const isTruncated = pairEntries.length > PAIR_PREVIEW_COUNT;
  const visible =
    isTruncated && !expanded
      ? pairEntries.slice(0, PAIR_PREVIEW_COUNT)
      : pairEntries;

  return (
    <>
      {captions.map((text) => (
        <BlockCaption key={text}>{text}</BlockCaption>
      ))}

      {/* Some territories carry only a caption ("No provinces; Singapore is
          a city-state."), with no pairs at all. */}
      {pairEntries.length > 0 && (
        <>
          <PairGrid>
            {visible.map(([name, capital]) => (
              <PairTile key={name}>
                <PairName>{name}</PairName>
                <PairValue>{capital}</PairValue>
              </PairTile>
            ))}
          </PairGrid>
          {isTruncated && (
            <ExpandButton
              type="button"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
            >
              {expanded ? showLessLabel : showAllLabel(pairEntries.length)}
            </ExpandButton>
          )}
        </>
      )}

      {lists.map(([label, items]) => (
        <CollapsibleItemGrid
          key={label}
          label={label}
          items={items}
          showAllLabel={showAllLabel}
          showLessLabel={showLessLabel}
        />
      ))}

      {notes.map((text) => (
        <BlockNote key={text}>{text}</BlockNote>
      ))}
    </>
  );
};

export const Country = () => {
  const { countryName } = useParams();
  const navigate = useNavigate();
  const { language } = useAppLanguage();
  const countryKey = countryName ? findCountryKey(countryName) : undefined;
  const [searchParams] = useSearchParams();
  const thread = findRouteThread(
    readRouteParam(searchParams),
    countryKey?.englishName
  );
  // Remembered across country pages: someone comparing everything against
  // Chile wants to keep comparing against Chile, not re-pick it each time.
  const [comparedName, setComparedName] = useState<string>(() => {
    try {
      return window.localStorage.getItem(COMPARE_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [flagFailed, setFlagFailed] = useState(false);

  // Resolved to the article rather than kept as a slug, so the link lands on
  // the URL of whichever language is being read.
  const disputeTopic = findLearnTopic(
    countryKey?.englishName
      ? SOVEREIGNTY_DISPUTE_LEARN_SLUG[countryKey.englishName]
      : undefined
  );

  const resolvedLanguage: AvailableLanguages =
    language ??
    (countryKey?.source === "spanish"
      ? AvailableLanguages.ES
      : AvailableLanguages.EN);
  const isSpanish = resolvedLanguage === AvailableLanguages.ES;

  const ui = {
    en: {
      back: "Back",
      backAria: "Go back",
      flagOf: (name: string) => `Flag of ${name}`,
      locatorOf: (name: string) => `Where ${name} sits in its continent`,
      worldMedian: (value: string, count: number) =>
        `world median ${value}, across ${count} countries`,
      unusualTitle: (name: string) => `What stands out about ${name}`,
      aboveMedian: (percent: number) => `higher than ${percent}% of countries`,
      belowMedian: (percent: number) => `lower than ${percent}% of countries`,
      flagUnavailable: "Flag unavailable",
      noInfoTitle: "Information unavailable",
      noInfoBody: (name: string) =>
        `We couldn’t find data for ${name}. Try going back and selecting the country from the map.`,
      quickFactsTitle: "Country facts",
      againstTheWorld: "measured against the world",
      compareWith: "Compare with",
      compareNone: "the world only",
      threadEyebrow: "YOU ARE FOLLOWING",
      threadPosition: (n: number, total: number) => `stop ${n} of ${total}`,
      threadPrevious: (name: string) => `Back to ${name}`,
      threadNext: (name: string) => `Next stop: ${name}`,
      threadBackToRoute: "See the whole route",
      shortcutMap: "Map",
      shortcutLearn: "Learn",
      shortcutsLabel: "Shortcuts",
      quickFacts: {
        continent: "Continent",
        capital: "Capital",
        population: "Population",
        currency: "Currency",
        language: "Language",
        timeZone: "Time zone",
        size: "Size",
      },
      showItems: (n: number) => `Show ${n} items`,
      showDetails: (n: number) => `Show details (${n})`,
      showAll: (n: number) => `Show all ${n}`,
      heritageCount: (n: number) =>
        n === 1 ? "1 entry on the list" : `${n} entries on the list`,
      intangibleNote: "",
      showLess: "Show less",
      listenTo: (text: string) => `Listen: ${text}`,
      disputeBanner:
        "This territory's sovereignty is disputed — read Argentina's claim →",
    },
    es: {
      back: "Volver",
      backAria: "Volver",
      flagOf: (name: string) => `Bandera de ${name}`,
      locatorOf: (name: string) => `Dónde queda ${name} en su continente`,
      worldMedian: (value: string, count: number) =>
        `mediana mundial ${value}, sobre ${count} países`,
      unusualTitle: (name: string) => `Lo que distingue a ${name}`,
      aboveMedian: (percent: number) =>
        `más alto que el ${percent}% de los países`,
      belowMedian: (percent: number) =>
        `más bajo que el ${percent}% de los países`,
      flagUnavailable: "Bandera no disponible",
      noInfoTitle: "Información no disponible",
      noInfoBody: (name: string) =>
        `No encontramos datos para ${name}. Probá volver atrás y seleccionar el país desde el mapa.`,
      quickFactsTitle: "Datos del país",
      againstTheWorld: "comparados con el mundo",
      compareWith: "Comparar con",
      compareNone: "solo el mundo",
      threadEyebrow: "ESTÁS SIGUIENDO",
      threadPosition: (n: number, total: number) => `parada ${n} de ${total}`,
      threadPrevious: (name: string) => `Volver a ${name}`,
      threadNext: (name: string) => `Próxima parada: ${name}`,
      threadBackToRoute: "Ver la ruta completa",
      shortcutMap: "Mapa",
      shortcutLearn: "Aprender",
      shortcutsLabel: "Atajos",
      quickFacts: {
        continent: "Continente",
        capital: "Capital",
        population: "Población",
        currency: "Moneda",
        language: "Idioma",
        timeZone: "Zona horaria",
        size: "Tamaño",
      },
      showItems: (n: number) => `Mostrar ${n} elementos`,
      showDetails: (n: number) => `Mostrar detalles (${n})`,
      showAll: (n: number) => `Ver las ${n}`,
      heritageCount: (n: number) =>
        n === 1 ? "1 elemento en la lista" : `${n} elementos en la lista`,
      intangibleNote:
        "UNESCO publica estos títulos solo en inglés y francés; la traducción al castellano es nuestra.",
      showLess: "Ver menos",
      listenTo: (text: string) => `Escuchar: ${text}`,
      disputeBanner:
        "La soberanía de este territorio está en disputa — conocé el reclamo argentino →",
    },
  } as const;

  const t = isSpanish ? ui.es : ui.en;

  // A stop names its country in English; the band has to show it the way the
  // reader is reading the rest of the page.
  const stopDisplayName = (englishName: string) => {
    const match = (countriesData as any[]).find(
      (entry) => entry["english-name"] === englishName
    );
    return (
      (isSpanish ? match?.["spanish-name"] : match?.["english-name"]) ??
      englishName
    );
  };

  const displayName =
    (isSpanish ? countryKey?.spanishName : countryKey?.englishName) ??
    countryName ??
    (isSpanish ? "este país" : "this country");

  const primaryCountryInfo = isSpanish
    ? countryKey?.spanishName
      ? (SpanishData as Record<string, unknown>)[countryKey.spanishName]
      : undefined
    : countryKey?.englishName
      ? (EnglishData as Record<string, unknown>)[countryKey.englishName]
      : undefined;

  // Fallback: if a country exists in `countriesData.json` but is missing from the
  // selected-language dataset, show the other-language dataset instead of
  // "information unavailable".
  const fallbackCountryInfo = isSpanish
    ? countryKey?.englishName
      ? (EnglishData as Record<string, unknown>)[countryKey.englishName]
      : undefined
    : countryKey?.spanishName
      ? (SpanishData as Record<string, unknown>)[countryKey.spanishName]
      : undefined;

  const countryInfo = primaryCountryInfo ?? fallbackCountryInfo;

  const info = (countryInfo ?? undefined) as CountryInfo | undefined;
  const hasInfo = !!info && typeof info === "object";

  const iso2 = countryKey?.initials
    ? countryKey.initials.toLowerCase()
    : undefined;
  const flagUrl = iso2 ? `https://flagcdn.com/w160/${iso2}.png` : undefined;
  const flagUrl2x = iso2 ? `https://flagcdn.com/w320/${iso2}.png` : undefined;

  const getFirstValue = (keys: string[]) => {
    if (!info) return undefined;
    for (const k of keys) {
      const v = info[k];
      if (typeof v === "string" && v.trim().length > 0) return v.trim();
      if (typeof v === "number") return String(v);
    }
    return undefined;
  };

  const quickFacts = [
    {
      label: t.quickFacts.continent,
      value: getFirstValue(["Continent", "Continente"]),
    },
    { label: t.quickFacts.capital, value: getFirstValue(["Capital"]) },
    {
      label: t.quickFacts.population,
      value: getFirstValue(["Population", "Cantidad de Población"]),
    },
    {
      label: t.quickFacts.currency,
      value: getFirstValue(["Currency", "Moneda"]),
    },
    {
      label: t.quickFacts.language,
      value: getFirstValue(["Language", "Idioma"]),
    },
    {
      label: t.quickFacts.timeZone,
      value: getFirstValue(["Time Zones", "Zonas Horarias"]),
    },
    {
      label: t.quickFacts.size,
      // 55 countries carry "Area" instead of "Size"; without the alias their
      // size silently vanished from the quick facts.
      value: getFirstValue(["Size", "Tamaño", "Area", "Área"]),
    },
  ].filter((f) => !!f.value);

  const currencyCode = extractCurrencyCode(
    getFirstValue(["Currency", "Moneda"])
  );

  const omitKeys = new Set<string>([
    "Flag",
    "Bandera",
    "Continent",
    "Continente",
    "Capital",
    "Population",
    "Cantidad de Población",
    "Currency",
    "Moneda",
    "Language",
    "Idioma",
    "Time Zones",
    "Zonas Horarias",
    "Size",
    "Tamaño",
    "Area",
    "Área",
  ]);

  const remainingEntries: Array<[string, unknown]> = info
    ? Object.entries(info).filter(
        ([k, v]) => !omitKeys.has(k) && v !== "" && v !== null
      )
    : [];

  // Only countries that carry an indicator block: offering one with nothing
  // to compare would be a dead option.
  const comparableCountries = (countriesData as any[])
    .filter((country) => !country["not-a-country"])
    .map((country) => {
      const key = isSpanish ? country["spanish-name"] : country["english-name"];
      return { key, label: key };
    })
    .filter(({ key }) => {
      if (
        key === (isSpanish ? countryKey?.spanishName : countryKey?.englishName)
      )
        return false;
      const entry = (
        (isSpanish ? SpanishData : EnglishData) as Record<string, any>
      )[key];
      const block = entry?.["Índices"] ?? entry?.["Indexes"];
      return !!block && typeof block === "object";
    })
    .sort((a, b) => a.label.localeCompare(b.label, isSpanish ? "es" : "en"));

  const comparedIndicators: Record<string, string> | undefined = (() => {
    if (!comparedName) return undefined;
    const entry = (
      (isSpanish ? SpanishData : EnglishData) as Record<string, any>
    )[comparedName];
    const block = entry?.["Índices"] ?? entry?.["Indexes"];
    return block && typeof block === "object" ? block : undefined;
  })();

  const groupedSections = groupCountryFields(remainingEntries, isSpanish);

  // Both UNESCO lists come out of the generic renderer as a long strip of
  // chips, so they get a grid of their own. Whether that grid is worth the
  // full page width depends on the country — see WIDE_LIST_MIN_ITEMS.
  const heritageLists = ["worldHeritage", "intangibleHeritage"]
    .map((id) =>
      groupedSections
        .flatMap((section) => section.fields)
        .find((field) => field.id === id && Array.isArray(field.value))
    )
    .filter((field): field is NonNullable<typeof field> => !!field);

  // Eleven measured figures are the most valuable thing on the page, and in a
  // masonry column the generic renderer folded them away behind "show details
  // (12)". They get their own full-width strip of tiles instead.
  const indicators = groupedSections
    .flatMap((section) => section.fields)
    .find((field) => field.id === "indexes" && isPairMap(field.value));

  const sections = groupedSections
    .map((section) => ({
      ...section,
      fields: section.fields.filter(
        (field) => !heritageLists.includes(field) && field !== indicators
      ),
    }))
    .filter((section) => section.fields.length > 0);

  // A full-width strip only earns its width if it has enough in it to fill a
  // row. Malta's three World Heritage sites spanned 1400px with 1100px of
  // empty card beside them; below the threshold the block joins the masonry,
  // where a 480px column fits those same three tiles exactly.
  const isWideList = (n: number) => n >= WIDE_LIST_MIN_ITEMS;

  const wideHeritage = heritageLists.filter((field) =>
    isWideList((field.value as string[]).length)
  );
  const narrowHeritage = heritageLists.filter(
    (field) => !wideHeritage.includes(field)
  );

  const divisionsSection = sections.find(
    (section) => section.id === "divisions"
  );

  // Singapore's whole divisions block is one sentence ("no provinces; it is a
  // city-state"); Russia's is 85 seats. Same rule as the heritage lists.
  const divisionEntryCount = (() => {
    const field = divisionsSection?.fields.find(
      (f) => f.id === "divisionCapitals"
    );
    if (!field || !isPairMap(field.value)) return 0;
    const { pairs, lists } = splitPairsAndLists(
      splitBlockMeta(field.value).entries
    );
    return (
      pairs.length + lists.reduce((total, [, items]) => total + items.length, 0)
    );
  })();
  const divisionsAreWide = isWideList(divisionEntryCount);

  const masonrySections = sections.filter(
    (section) => section.id !== "divisions" || !divisionsAreWide
  );

  const labelForExtraKey = (key: string) => {
    if (key === "extra-details")
      return isSpanish ? "Detalles extra" : "Extra details";
    if (key === "extra-list") return isSpanish ? "Lista extra" : "Extra list";
    return key;
  };

  const normalizeArrayToStrings = (arr: unknown[]) =>
    arr
      .flatMap((item) => {
        if (typeof item === "string" || typeof item === "number")
          return [String(item)];
        if (typeof item === "object" && item !== null) {
          return Object.values(item as Record<string, unknown>).flatMap((v) =>
            typeof v === "string" || typeof v === "number" ? [String(v)] : []
          );
        }
        return [];
      })
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

  const getSpeakableText = (
    key: string,
    value: unknown
  ): string | undefined => {
    if (typeof value === "string" || typeof value === "number") {
      return `${key}: ${value}`;
    }
    if (Array.isArray(value)) {
      const items = normalizeArrayToStrings(value);
      if (items.length === 0) return undefined;
      return `${key}: ${items.join(", ")}`;
    }
    // Climate is stored as raw measurements, so the sentence a listener
    // should hear is the one the card renders, not the field names.
    if (isCountryClimate(value)) {
      return describeClimate(value, isSpanish).spoken;
    }
    // Dict-valued fields (Music, Sport, Education) used to fall through to
    // undefined, which silently dropped their listen button — the only
    // fields on the page a screen reader or a learner couldn't hear.
    if (typeof value === "object" && value !== null) {
      const parts = Object.entries(value as Record<string, unknown>)
        // The source footnote is the same sentence on every country; reading
        // it out on each play would bury the data it annotates.
        .filter(
          ([subKey]) => !/^(nota|note|fuente|source)$/i.test(subKey.trim())
        )
        .map(([subKey, subValue]) => getSpeakableText(subKey, subValue))
        .filter((part): part is string => !!part);
      if (parts.length === 0) return undefined;
      return `${key}. ${parts.join(". ")}`;
    }
    return undefined;
  };

  const renderValue = (value: unknown) => {
    if (typeof value === "string" || typeof value === "number") {
      return <ValueText>{String(value)}</ValueText>;
    }

    if (Array.isArray(value)) {
      const items = normalizeArrayToStrings(value);

      if (items.length === 0) return <ValueText>{String(value)}</ValueText>;

      // Wider cards fit a lot more chips inline than the old narrow column,
      // so only genuinely huge lists get folded away.
      if (items.length > 28) {
        return (
          <Details>
            <Summary>{t.showItems(items.length)}</Summary>
            <Chips>
              {items.map((v) => (
                <Chip key={v}>{v}</Chip>
              ))}
            </Chips>
          </Details>
        );
      }

      return (
        <Chips>
          {items.map((v) => (
            <Chip key={v}>{v}</Chip>
          ))}
        </Chips>
      );
    }

    if (typeof value === "object" && value !== null) {
      const obj = value as Record<string, unknown>;
      const { captions, notes, entries } = splitBlockMeta(obj);
      const hasExtraDetails =
        typeof obj["extra-details"] === "string" &&
        obj["extra-details"].trim().length > 0;

      const definitionList = (
        <DefinitionList>
          {entries.map(([k, v]) => (
            <div key={k}>
              {!(k === "extra-list" && hasExtraDetails) && (
                <DT>{labelForExtraKey(k)}</DT>
              )}
              <DD>
                {typeof v === "string" || typeof v === "number"
                  ? String(v)
                  : Array.isArray(v)
                    ? (() => {
                        const items = normalizeArrayToStrings(v);
                        if (items.length === 0) return String(v);
                        return (
                          <BulletList>
                            {items.map((text, idx) => (
                              <BulletItem key={`${k}-${idx}`}>
                                <BulletText>{text}</BulletText>
                              </BulletItem>
                            ))}
                          </BulletList>
                        );
                      })()
                    : String(v)}
              </DD>
            </div>
          ))}
        </DefinitionList>
      );

      return (
        <>
          {captions.map((text) => (
            <BlockCaption key={text}>{text}</BlockCaption>
          ))}
          {/* The caption stays outside the collapse: it's what tells you
              whether the block is worth expanding. Small objects (a few
              indexes, an "extra information" block) read better expanded. */}
          {entries.length > 0 &&
            (entries.length <= 10 ? (
              definitionList
            ) : (
              <Details>
                <Summary>{t.showDetails(entries.length)}</Summary>
                {definitionList}
              </Details>
            ))}
          {notes.map((text) => (
            <BlockNote key={text}>{text}</BlockNote>
          ))}
        </>
      );
    }
    return <ValueText>{String(value)}</ValueText>;
  };

  const renderField = (field: CountryField) => {
    // Province/state → capital maps get their own grid instead of the
    // generic definition list: they're the bulkiest value in the dataset and
    // the one the old narrow column handled worst.
    if (field.id === "climate" && isCountryClimate(field.value)) {
      return <ClimateValue climate={field.value} isSpanish={isSpanish} />;
    }
    if (field.id === "divisionCapitals" && isPairMap(field.value)) {
      return (
        <PairMapValue
          pairs={field.value}
          showAllLabel={t.showAll}
          showLessLabel={t.showLess}
        />
      );
    }
    return renderValue(field.value);
  };

  const sectionHeader = (emoji: string, title: string) => (
    <SectionHeader>
      <SectionEmoji aria-hidden="true">{emoji}</SectionEmoji>
      <CardTitle>{title}</CardTitle>
    </SectionHeader>
  );

  const renderSectionBody = (section: (typeof sections)[number]) => {
    // The divisions section is two fields that belong together: a one-line
    // summary ("23 provinces and one Autonomous City") and the seats. Boxing
    // the summary as a field of its own gave it a card, a heading and a
    // border to carry eight words. It's the subtitle of the grid below it.
    const isDivisions = section.id === "divisions";
    const summary = isDivisions
      ? section.fields.find(
          (field) => field.id === "divisions" && typeof field.value === "string"
        )
      : undefined;
    const bodyFields = section.fields.filter((field) => field !== summary);
    // With the summary as the subtitle, "Capitals" over a grid of
    // region → seat tiles is a label for something already self-evident.
    const hideFieldNames =
      isDivisions &&
      bodyFields.every((field) => field.id === "divisionCapitals");

    return (
      <>
        {sectionHeader(section.emoji, section.title)}

        {summary && (
          <SectionSubtitle>
            {String(summary.value)}
            <SpeakButton
              text={getSpeakableText(summary.label, summary.value)}
              language={resolvedLanguage}
              label={t.listenTo(summary.label)}
            />
          </SectionSubtitle>
        )}

        <FieldList>
          {bodyFields.map((field) => (
            <FieldBlock
              key={field.rawKey}
              $wide={hideFieldNames || fieldWantsFullRow(field.value)}
              $bare={hideFieldNames}
            >
              {!hideFieldNames && (
                <FieldName>
                  {field.label}
                  <SpeakButton
                    text={getSpeakableText(field.label, field.value)}
                    language={resolvedLanguage}
                    label={t.listenTo(field.label)}
                  />
                </FieldName>
              )}
              {renderField(field)}
            </FieldBlock>
          ))}
        </FieldList>
      </>
    );
  };

  const renderHeritageBlock = (
    field: CountryField,
    { wide }: { wide: boolean }
  ) => (
    <>
      {sectionHeader(field.id === "worldHeritage" ? "🏛️" : "🎎", field.label)}
      <CollapsibleItemGrid
        label={t.heritageCount((field.value as string[]).length)}
        items={field.value as string[]}
        showAllLabel={t.showAll}
        showLessLabel={t.showLess}
        minTile={wide ? HERITAGE_TILE_MIN : undefined}
      />
      {/* UNESCO only publishes these titles in English and French, so the
          Spanish reader is seeing our translation. Say so rather than pass
          it off as the official denomination. */}
      {field.id === "intangibleHeritage" && isSpanish && (
        <BlockNote>{t.intangibleNote}</BlockNote>
      )}
    </>
  );

  return (
    <Page id="country" lang={isSpanish ? "es" : "en"}>
      <Bubbles />
      <WideContent>
        <TopBar>
          <BackButton onClick={() => navigate(-1)} aria-label={t.backAria}>
            <span aria-hidden="true">←</span> {t.back}
          </BackButton>
        </TopBar>

        <Stack>
          {thread && (
            <Thread aria-label={thread.route[isSpanish ? "es" : "en"].title}>
              <ThreadTop>
                <ThreadEyebrow>{t.threadEyebrow}</ThreadEyebrow>
                <ThreadRoute to={routeHref(isSpanish, thread.route)}>
                  {thread.route.emoji}{" "}
                  {thread.route[isSpanish ? "es" : "en"].title}
                </ThreadRoute>
                <ThreadPosition>
                  {t.threadPosition(thread.position, thread.total)}
                </ThreadPosition>
              </ThreadTop>

              <ThreadTitle>
                {thread.stop[isSpanish ? "es" : "en"].title}
              </ThreadTitle>
              <ThreadBody>
                {thread.stop[isSpanish ? "es" : "en"].body}
              </ThreadBody>

              <ThreadNav>
                {thread.previous && (
                  <ThreadStep
                    to={countryHref(
                      isSpanish,
                      stopDisplayName(thread.previous.country),
                      thread.route
                    )}
                  >
                    <span aria-hidden="true">←</span>{" "}
                    {t.threadPrevious(stopDisplayName(thread.previous.country))}
                  </ThreadStep>
                )}
                {thread.next && (
                  <ThreadStep
                    to={countryHref(
                      isSpanish,
                      stopDisplayName(thread.next.country),
                      thread.route
                    )}
                  >
                    {t.threadNext(stopDisplayName(thread.next.country))}{" "}
                    <span aria-hidden="true">→</span>
                  </ThreadStep>
                )}
                <ThreadStep to={routeHref(isSpanish, thread.route)}>
                  {t.threadBackToRoute}
                </ThreadStep>
              </ThreadNav>
            </Thread>
          )}

          <HeroCard>
            {flagUrl && !flagFailed ? (
              <Flag
                src={flagUrl}
                srcSet={flagUrl2x ? `${flagUrl2x} 2x` : undefined}
                alt={t.flagOf(displayName)}
                onError={() => setFlagFailed(true)}
                decoding="async"
              />
            ) : (
              <Flag
                src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='92' height='62'%3E%3Crect width='92' height='62' fill='rgba(245,226,200,0.08)'/%3E%3Cpath d='M14 42c12-10 20-18 32-18s20 8 32 18' fill='none' stroke='rgba(245,226,200,0.35)' stroke-width='3'/%3E%3Ccircle cx='30' cy='20' r='5' fill='rgba(245,226,200,0.35)'/%3E%3C/svg%3E"
                alt={t.flagUnavailable}
              />
            )}

            {countryKey?.englishName && (
              <ContinentLocator
                englishName={countryKey.englishName}
                label={t.locatorOf(displayName)}
              />
            )}

            <HeroText>
              <TitleRow>
                <MainTitle title={displayName} />
                <SpeakButton
                  text={displayName}
                  language={resolvedLanguage}
                  label={t.listenTo(displayName)}
                />
              </TitleRow>

              <PillsRow>
                {getFirstValue(["Seas and Oceans", "Mares y Océanos"]) && (
                  <Pill>
                    <span aria-hidden="true">🌊</span>{" "}
                    {getFirstValue(["Seas and Oceans", "Mares y Océanos"])}
                  </Pill>
                )}
                {getFirstValue([
                  "Bordering Countries",
                  "Países Limítrofes",
                ]) && (
                  <Pill>
                    <span aria-hidden="true">🧭</span>{" "}
                    {getFirstValue([
                      "Bordering Countries",
                      "Países Limítrofes",
                    ])}
                  </Pill>
                )}
              </PillsRow>
            </HeroText>
          </HeroCard>

          {disputeTopic && (
            <DisputeBanner to={topicHref(isSpanish, disputeTopic)}>
              <span aria-hidden="true">⚖️</span> {t.disputeBanner}
            </DisputeBanner>
          )}

          {!hasInfo ? (
            <Card>
              <CardTitle>{t.noInfoTitle}</CardTitle>
              <ValueText>{t.noInfoBody(displayName)}</ValueText>
            </Card>
          ) : (
            <>
              {/* One card, because the old split was arbitrary: population
                  was a "quick fact" and urban population an "indicator",
                  two numbers about the same people separated by nothing but
                  where the dataset happened to put them. Summary first,
                  then the reference facts, then the measured ones. */}
              <Card>
                <CardTitle>{t.quickFactsTitle}</CardTitle>

                <FactsGrid>
                  {quickFacts.map((f) => (
                    <Fact
                      key={f.label}
                      $wide={(f.value?.length ?? 0) > WIDE_FACT_CHARS}
                    >
                      <FactLabel>{f.label}</FactLabel>
                      <FactValue>
                        {f.value}
                        <SpeakButton
                          text={`${f.label}: ${f.value}`}
                          language={resolvedLanguage}
                          label={t.listenTo(f.label)}
                        />
                      </FactValue>
                      {f.label === t.quickFacts.currency && currencyCode && (
                        <CurrencyToUsd
                          code={currencyCode}
                          isSpanish={isSpanish}
                        />
                      )}
                    </Fact>
                  ))}
                </FactsGrid>

                {indicators && (
                  <>
                    {(() => {
                      const entries = Object.entries(
                        indicators.value as Record<string, string>
                      );
                      // The source line describes the whole block, so it reads
                      // as a footnote rather than a twelfth statistic.
                      const isSource = ([key]: [string, string]) =>
                        /^(source|fuente)$/i.test(key.trim());
                      const stats = entries.filter((e) => !isSource(e));
                      const source = entries.find(isSource)?.[1];

                      const measured = stats.map(([label, value]) => {
                        const other = comparedIndicators?.[label];
                        const otherContext =
                          typeof other === "string"
                            ? indicatorContext(label, other, isSpanish)
                            : undefined;
                        return {
                          label,
                          value,
                          context: indicatorContext(label, value, isSpanish),
                          // Absent when the other country has no figure for
                          // this indicator, which is common: the Gini exists
                          // for 153 countries of 199.
                          compared: otherContext
                            ? { context: otherContext }
                            : undefined,
                        };
                      });

                      const unusual = measured
                        .filter((item) => !!item.context)
                        .sort(
                          (a, b) =>
                            (b.context?.distanceFromMedian ?? 0) -
                            (a.context?.distanceFromMedian ?? 0)
                        )
                        .slice(0, 3);

                      return (
                        <>
                          <GroupLabel>
                            {indicators.label.toUpperCase()} ·{" "}
                            {t.againstTheWorld}
                          </GroupLabel>

                          <CompareRow>
                            <CompareLabel htmlFor="compare-country">
                              {t.compareWith.toUpperCase()}
                            </CompareLabel>
                            <CompareSelect
                              id="compare-country"
                              value={comparedName}
                              onChange={(event) => {
                                const next = event.target.value;
                                setComparedName(next);
                                try {
                                  window.localStorage.setItem(
                                    COMPARE_KEY,
                                    next
                                  );
                                } catch {
                                  // A blocked store costs the memory, nothing else.
                                }
                              }}
                            >
                              <option value="">{t.compareNone}</option>
                              {comparableCountries.map((option) => (
                                <option key={option.key} value={option.key}>
                                  {option.label}
                                </option>
                              ))}
                            </CompareSelect>
                          </CompareRow>

                          {unusual.length === 3 && (
                            <Unusual>
                              <UnusualTitle>
                                {t.unusualTitle(displayName).toUpperCase()}
                              </UnusualTitle>
                              {unusual.map(({ label, value, context }) => {
                                const above = (context?.percentile ?? 50) >= 50;
                                const share = Math.round(
                                  above
                                    ? (context?.shareBelow ?? 0)
                                    : (context?.shareAbove ?? 0)
                                );
                                return (
                                  <UnusualLine key={label}>
                                    <span>{label.toLowerCase()}</span>
                                    <b>
                                      {shortIndicatorValue(value, isSpanish)}
                                    </b>
                                    <span>
                                      —{" "}
                                      {above
                                        ? t.aboveMedian(share)
                                        : t.belowMedian(share)}
                                    </span>
                                  </UnusualLine>
                                );
                              })}
                            </Unusual>
                          )}

                          <FactsGrid>
                            {measured.map(
                              ({ label, value, context, compared }) => (
                                <Fact key={label}>
                                  <FactLabel>{label}</FactLabel>
                                  <FactValue>{value}</FactValue>
                                  {context && (
                                    <>
                                      <MedianTrack
                                        role="img"
                                        aria-label={t.worldMedian(
                                          formatIndicatorNumber(
                                            context.median,
                                            isSpanish
                                          ),
                                          context.count
                                        )}
                                      >
                                        <MedianFill
                                          $above={context.percentile >= 50}
                                          style={{
                                            width: `${context.percentile}%`,
                                          }}
                                        />
                                        <MedianMark />
                                        {compared && (
                                          <CompareMark
                                            style={{
                                              left: `${compared.context.percentile}%`,
                                            }}
                                          />
                                        )}
                                      </MedianTrack>
                                      <MedianNote>
                                        {t.worldMedian(
                                          formatIndicatorNumber(
                                            context.median,
                                            isSpanish
                                          ),
                                          context.count
                                        )}
                                      </MedianNote>
                                      {compared && (
                                        <CompareNote>
                                          {comparedName}{" "}
                                          {formatIndicatorNumber(
                                            compared.context.value,
                                            isSpanish
                                          )}
                                        </CompareNote>
                                      )}
                                    </>
                                  )}
                                </Fact>
                              )
                            )}
                          </FactsGrid>
                          {source && <BlockNote>{source}</BlockNote>}
                        </>
                      );
                    })()}
                  </>
                )}
              </Card>

              {masonrySections.length + narrowHeritage.length > 0 && (
                <SectionsMasonry>
                  {masonrySections.map((section) => (
                    <SectionCard key={section.id}>
                      {renderSectionBody(section)}
                    </SectionCard>
                  ))}
                  {narrowHeritage.map((field) => (
                    <SectionCard key={field.id}>
                      {renderHeritageBlock(field, { wide: false })}
                    </SectionCard>
                  ))}
                </SectionsMasonry>
              )}

              {wideHeritage.map((field) => (
                <Card key={field.id}>
                  {renderHeritageBlock(field, { wide: true })}
                </Card>
              ))}

              {divisionsSection && divisionsAreWide && (
                <Card key={divisionsSection.id}>
                  {renderSectionBody(divisionsSection)}
                </Card>
              )}
            </>
          )}
        </Stack>
      </WideContent>

      <Shortcuts aria-label={t.shortcutsLabel}>
        <Shortcut to="/">
          <span aria-hidden="true">🗺️</span> {t.shortcutMap}
        </Shortcut>
        <Shortcut to={learnHref(isSpanish)}>
          <span aria-hidden="true">📚</span> {t.shortcutLearn}
        </Shortcut>
      </Shortcuts>
    </Page>
  );
};
